// The preview-first copy: the quick web-size JPEG the web uploader sends before the original
// (useUploadManager). Kept in a module of its own so pages that only need its size (the upload-time
// estimator, src/lib/uploader/uploadEstimate.ts) don't pull in the upload manager.

/** Long edge of the preview-first copy. 1200 covers every render buyers get before the original lands
 *  (grid md 900, lightbox md/lg 1200, protected galleries lg 1200) and every detector input (640). The
 *  face / person crops that need more come from the original (crop-back, src/lib/uploader/cropBack.ts). */
export const PREVIEW_EDGE = 1200;

/** JPEG quality of the copy. q75 is preview-grade (≈150–250 KB from a 20MP race photo at 1200px, vs
 *  ≈310–490 KB at the old 2048px), and it keeps the copy pass fast. */
export const PREVIEW_QUALITY = 0.75;

/** A size-based guess at a resized JPEG's bytes before any has been measured: a 3:2 frame at `maxEdge`
 *  on the long side, ~0.2 bytes per pixel at q85, scaling with the square of the quality. */
export const estimatedCopyBytes = (maxEdge: number, quality = 0.85): number =>
  maxEdge * maxEdge * 0.667 * 0.2 * (quality / 0.85) ** 2;

// --- The uploader's own preview size (upload summary → "Adjust") ------------------------------------------------
// The defaults above stay the defaults; the person can pick another long edge / quality per browser. JPEG only.
// Floors are hard: 640px is the detectors' input (on-device scan and the server both detect at 640), and below
// q0.70 a copy stops being a usable preview. The server takes the copy's frame from the copy itself (EXIF
// width/height → build.steps.scanFrame), and crop-back cuts from the original, so any size in range is safe.

export type PreviewSize = { maxEdge: number; quality: number };

export const PREVIEW_EDGE_MIN = 640;
export const PREVIEW_EDGE_MAX = 2048;
/** The long edges offered (the control snaps to these). */
export const PREVIEW_EDGE_STEPS = [640, 800, 1024, 1200, 1600, 2048] as const;
export const PREVIEW_QUALITY_MIN = 0.7;
export const PREVIEW_QUALITY_MAX = 0.95;
/** Below this long edge, buyers' full-screen view may look slightly soft until the original arrives. */
export const PREVIEW_EDGE_SOFT_BELOW = 1200;

export const DEFAULT_PREVIEW_SIZE: PreviewSize = { maxEdge: PREVIEW_EDGE, quality: PREVIEW_QUALITY };

const num = (v: unknown, fallback: number): number => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
};

/** Any preview size, made safe: long edge snapped to the nearest offered step within 640–2048, quality to
 *  5% steps within 0.70–0.95. Missing / garbage values fall back to the defaults. */
export const clampPreviewSize = (s?: Partial<PreviewSize> | null): PreviewSize => {
  const edge = Math.min(PREVIEW_EDGE_MAX, Math.max(PREVIEW_EDGE_MIN, num(s?.maxEdge, PREVIEW_EDGE)));
  const maxEdge = PREVIEW_EDGE_STEPS.reduce((best, x) => (Math.abs(x - edge) < Math.abs(best - edge) ? x : best), PREVIEW_EDGE_STEPS[0] as number);
  const q = Math.min(PREVIEW_QUALITY_MAX, Math.max(PREVIEW_QUALITY_MIN, num(s?.quality, PREVIEW_QUALITY)));
  const quality = Math.min(PREVIEW_QUALITY_MAX, Math.max(PREVIEW_QUALITY_MIN, Math.round(q * 20) / 20));
  return { maxEdge, quality };
};

export const isDefaultPreviewSize = (s: PreviewSize): boolean =>
  s.maxEdge === DEFAULT_PREVIEW_SIZE.maxEdge && Math.abs(s.quality - DEFAULT_PREVIEW_SIZE.quality) < 1e-9;

/** Remembered per browser, like the previews-first choice. Always clamped on the way in and out. */
export const PREVIEW_SIZE_KEY = "ph_up_preview_size";

export const readPreviewSize = (): PreviewSize => {
  try {
    const raw = localStorage.getItem(PREVIEW_SIZE_KEY);
    return clampPreviewSize(raw ? (JSON.parse(raw) as Partial<PreviewSize>) : null);
  } catch {
    return { ...DEFAULT_PREVIEW_SIZE };
  }
};

export const writePreviewSize = (s: PreviewSize): void => {
  try {
    const c = clampPreviewSize(s);
    if (isDefaultPreviewSize(c)) localStorage.removeItem(PREVIEW_SIZE_KEY);
    else localStorage.setItem(PREVIEW_SIZE_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
};
